import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { CopyableRequest } from "@/components/studio/CopyableRequest";
import { WorkflowWebhookTriggersSection } from "@/components/studio/WorkflowWebhookTriggersSection";
import DeploySchedulesSection from "@/components/deploy/DeploySchedulesSection";
import {
  applicationsAPI,
  type Application,
} from "@/services/api/applications";
import type { WorkflowDocumentV2 } from "@/services/api/recipes";
import {
  applicationConfigHref,
  applicationReviewHref,
} from "@/utils/contextUrl";

/**
 * Workflow › API & triggers (design/os-reorganisation-ecrans § 5.1):
 * the two HTTP routes with the expected input document and copyable curl
 * examples, the key each route requires with the action to obtain it, and the
 * Workflow triggers (schedules and incoming webhooks, contract
 * wiki/architecture/workflow-webhook-trigger.md). No key value is ever
 * displayed here; a webhook secret is shown once, at creation or rotation.
 */

const linkClassName =
  "font-medium text-accent-foreground underline-offset-4 hover:underline";

export function workflowPublishedApplications(
  applications: Application[],
  agentId: string,
  workflowId: string,
): Application[] {
  return applications.filter((application) =>
    (application.public_surface?.workflows ?? []).some(
      (ref) => ref.agent_id === agentId && ref.workflow_id === workflowId,
    ),
  );
}

export function buildApplicationWorkflowCurl(
  publicURL: string,
  applicationKey: string,
  workflowId: string,
  inputExample: unknown,
): string {
  const base = publicURL.replace(/\/+$/, "");
  return [
    `curl -sS -X POST "${base}/api/v2/applications/${encodeURIComponent(applicationKey)}/workflows/${encodeURIComponent(workflowId)}/executions" \\`,
    '  -H "X-API-Key: $AGENTMAURICE_APPLICATION_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '${JSON.stringify({ input: inputExample })}'`,
  ].join("\n");
}

function hasDeclaredSchema(schema: unknown): boolean {
  return (
    Boolean(schema) &&
    typeof schema === "object" &&
    !Array.isArray(schema) &&
    Object.keys(schema as Record<string, unknown>).length > 0
  );
}

function withSection(href: string, section: string): string {
  return `${href}${href.includes("?") ? "&" : "?"}section=${section}`;
}

export function WorkflowApiTriggersPanel({
  organizationId,
  agentId,
  workflow,
  inputExample,
  agentSyncCurl,
  agentAsyncCurl,
  agentStatusCurl,
  notYetApplied,
  publicURL,
}: {
  organizationId: string;
  agentId: string;
  workflow: WorkflowDocumentV2;
  inputExample: unknown;
  agentSyncCurl: string;
  agentAsyncCurl: string;
  agentStatusCurl: string;
  notYetApplied: boolean;
  publicURL: string;
}) {
  const { t } = useTranslation();
  const applicationsQuery = useQuery({
    queryKey: ["applications", organizationId],
    queryFn: () => applicationsAPI.list(organizationId),
    enabled: Boolean(organizationId),
    staleTime: 30_000,
  });
  const applications = useMemo(
    () => (Array.isArray(applicationsQuery.data) ? applicationsQuery.data : []),
    [applicationsQuery.data],
  );
  const published = useMemo(
    () =>
      workflowPublishedApplications(
        applications,
        agentId,
        workflow.workflow_id,
      ),
    [agentId, applications, workflow.workflow_id],
  );
  const memberApplication = useMemo(
    () =>
      applications.find((application) =>
        (application.agents ?? []).some(
          (member) => member.agent_id === agentId,
        ),
      ) ?? null,
    [agentId, applications],
  );
  const [selectedKey, setSelectedKey] = useState("");
  const selectedApplication =
    published.find((application) => application.key === selectedKey) ??
    published[0] ??
    null;

  const agentKeysHref = (() => {
    const params = new URLSearchParams();
    if (organizationId) params.set("organizationId", organizationId);
    params.set("agentId", agentId);
    params.set("deploymentId", agentId);
    return `/studio/api-keys?${params.toString()}`;
  })();
  const accessKeysHref = (application: Application) =>
    withSection(
      applicationConfigHref("/studio/access", {
        organizationId,
        applicationKey: application.key,
        agentId: application.entrypoints?.chat?.agent_id ?? null,
      }),
      "keys",
    );
  const publishHref = memberApplication
    ? applicationReviewHref(memberApplication.id, {
        organizationId,
        applicationKey: memberApplication.key,
        agentId: memberApplication.entrypoints?.chat?.agent_id ?? null,
      })
    : "/studio/applications";
  const schemaDeclared = hasDeclaredSchema(workflow.input_schema);
  const applicationName = (application: Application) =>
    application.display_name || application.key;

  return (
    <Tabs defaultValue="http" data-testid="workflow-api-triggers">
      <TabsList>
        <TabsTrigger value="http">{t("workflowApi.httpTab")}</TabsTrigger>
        <TabsTrigger value="triggers" data-testid="workflow-triggers-tab">
          {t("workflowApi.triggersTab")}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="http" className="space-y-6 pt-5">
        {notYetApplied ? (
          <p className="rounded-lg border border-warning/30 bg-warning-soft p-3 text-sm text-warning">
            {t("agentSpec.apiAvailableAfterApply")}
          </p>
        ) : null}

        <section
          className="space-y-3 rounded-lg border border-border bg-card p-4"
          data-testid="workflow-api-key-requirement"
        >
          <h3 className="font-semibold">{t("workflowApi.keyTitle")}</h3>
          <div className="space-y-1 text-sm">
            <p className="font-medium text-foreground">
              {t("workflowApi.agentRouteLabel")}
            </p>
            <p className="text-muted-foreground">
              {t("workflowApi.agentKeyRequirement")}{" "}
              <Link
                to={agentKeysHref}
                className={linkClassName}
                data-testid="workflow-api-create-agent-key"
              >
                {t("workflowApi.createAgentKey")}
              </Link>
            </p>
          </div>
          <div className="space-y-1 text-sm">
            <p className="font-medium text-foreground">
              {t("workflowApi.applicationRouteLabel")}
            </p>
            <p className="text-muted-foreground">
              {t("workflowApi.applicationKeyRequirement")}
            </p>
            {published.length > 0 ? (
              <ul className="space-y-1" data-testid="workflow-api-published-apps">
                {published.map((application) => (
                  <li key={application.key} className="text-muted-foreground">
                    {t("workflowApi.publishedIn", {
                      name: applicationName(application),
                    })}{" "}
                    <Link
                      to={accessKeysHref(application)}
                      className={linkClassName}
                      data-testid={`workflow-api-create-app-key-${application.key}`}
                    >
                      {t("workflowApi.createApplicationKey")}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">
                {applicationsQuery.isLoading
                  ? t("common.loading")
                  : t("workflowApi.notPublished")}{" "}
                {applicationsQuery.isLoading ? null : (
                  <Link
                    to={publishHref}
                    className={linkClassName}
                    data-testid="workflow-api-publish"
                  >
                    {t("workflowApi.publishInApplication")}
                  </Link>
                )}
              </p>
            )}
          </div>
        </section>

        <section className="space-y-2" data-testid="workflow-api-input">
          <h3 className="font-semibold">{t("workflowApi.inputTitle")}</h3>
          <p className="text-sm text-muted-foreground">
            {schemaDeclared
              ? t("workflowApi.inputFromSchema")
              : t("workflowApi.inputGeneric")}
          </p>
          {schemaDeclared ? (
            <pre className="max-h-80 overflow-auto rounded-xl bg-muted p-4 font-mono text-xs text-foreground">
              {JSON.stringify(workflow.input_schema, null, 2)}
            </pre>
          ) : null}
        </section>

        <section className="space-y-3" data-testid="workflow-api-agent-route">
          <h3 className="font-semibold">{t("workflowApi.agentRouteTitle")}</h3>
          <h4 className="text-sm font-medium">
            {t("agentSpec.synchronousWorkflowCall")}
          </h4>
          <p className="text-sm text-muted-foreground">
            {t("agentSpec.synchronousWorkflowCallDescription")}
          </p>
          <CopyableRequest value={agentSyncCurl} testId="workflow-api-agent-sync" />
          <h4 className="text-sm font-medium">
            {t("agentSpec.asynchronousWorkflowCall")}
          </h4>
          <p className="text-sm text-muted-foreground">
            {t("agentSpec.asynchronousWorkflowCallDescription")}
          </p>
          <CopyableRequest
            value={agentAsyncCurl}
            testId="workflow-api-agent-async"
          />
          <h4 className="text-sm font-medium">
            {t("agentSpec.pollWorkflowExecution")}
          </h4>
          <CopyableRequest value={agentStatusCurl} />
        </section>

        <section className="space-y-3" data-testid="workflow-api-application-route">
          <h3 className="font-semibold">
            {t("workflowApi.applicationRouteTitle")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("workflowApi.applicationRouteSyncOnly")}
          </p>
          {selectedApplication ? (
            <>
              {published.length > 1 ? (
                <label className="block text-sm font-medium">
                  {t("workflowApi.applicationSelect")}
                  <select
                    value={selectedApplication.key}
                    onChange={(event) => setSelectedKey(event.target.value)}
                    className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2"
                  >
                    {published.map((application) => (
                      <option key={application.key} value={application.key}>
                        {applicationName(application)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <CopyableRequest
                value={buildApplicationWorkflowCurl(
                  publicURL,
                  selectedApplication.key,
                  workflow.workflow_id,
                  inputExample,
                )}
                testId="workflow-api-application-sync"
              />
            </>
          ) : (
            <p className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              {t("workflowApi.applicationRouteUnavailable")}
            </p>
          )}
        </section>
      </TabsContent>

      <TabsContent value="triggers" className="space-y-6 pt-5">
        <section className="space-y-2">
          <h3 className="font-semibold">{t("workflowApi.schedulesTitle")}</h3>
          <DeploySchedulesSection
            organizationId={organizationId}
            deploymentId={agentId}
            workflowId={workflow.workflow_id}
          />
        </section>
        <WorkflowWebhookTriggersSection
          organizationId={organizationId}
          agentId={agentId}
          workflowId={workflow.workflow_id}
          applications={published}
          applicationsLoading={applicationsQuery.isLoading}
          publicURL={publicURL}
          inputExample={inputExample}
        />
      </TabsContent>
    </Tabs>
  );
}
